// TypeScript types for Algo Configs

import { z } from "zod";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DEFAULT_ALGO_CONFIG,
  TIME_IN_FORCE_OPTIONS,
  PRICE_BASIS_OPTIONS,
  PRICE_OFFSET_OPTIONS,
  REFRESH_DISTANCE_OPTIONS,
  MIN_DISTANCE_OPTIONS,
  MAX_DISTANCE_OPTIONS,
  SCALE_TYPE_OPTIONS,
  SCALE_DIRECTION_OPTIONS,
  GRID_SIDE_OPTIONS,
  ORDER_SIDE_OPTIONS,
  ORDER_TYPE_OPTIONS,
  ChaseOrderConfig,
  ScaleOrderConfig,
  GridConfig,
  ImplementationShortfallConfig,
  AlgoConfig,
} from "./defaults";

import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Download, Upload, Pencil, Trash, Plus } from "lucide-react";
import { merge } from "lodash";
import { sfDefaultGrid } from "./presets/sfDefaultGrid";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";

const ChaseOrderSchema = z.object({
  AlgoOrderID: z.number(),
  UseAlgoOrder: z.boolean(),
  Hotkey: z.string(),
  Side: z.string(),
  Type: z.string(),
  TimeInForce: z.string(),
  Size: z.number(),
  SizeRandomRate: z.number(),
  PriceBasis: z.string(),
  ConstantPriceBasis: z.number(),
  PriceBasisMultiplier: z.number(),
  PriceOffset: z.string(),
  PriceOffsetMultiplier: z.number(),
  PriceRandomRate: z.number(),
  CancelDistance: z.string(),
  CancelDistanceMultiplier: z.number(),
  RefreshDistance: z.string(),
  CloseRefreshThresholdMultiplier: z.number(),
  FarRefreshThresholdMultiplier: z.number(),
  MinDistance: z.string(),
  MinDistanceMultiplier: z.number(),
  MaxDistance: z.string(),
  MaxDistanceMultiplier: z.number(),
  MinZScore: z.number(),
  MaxZScore: z.number(),
  WaitTime: z.number(),
  WaitTimeRandomRate: z.number(),
  RefreshTime: z.number(),
  RefreshTimeRandomRate: z.number(),
  RefreshNumber: z.number(),
  RefreshNumberRandomRate: z.number(),
  FreezingTime: z.number(),
  FreezingTimeRandomRate: z.number(),
  PauseTime: z.number(),
  PauseTimeRandomRate: z.number(),
  AutoContinuation: z.boolean(),
});

const ScaleOrderSchema = z.object({
  AlgoOrderID: z.number(),
  UseAlgoOrder: z.boolean(),
  Hotkey: z.string(),
  Side: z.string(),
  TimeInForce: z.string(),
  TotalSize: z.number(),
  SizeRandomRate: z.number(),
  SizeScaleType: z.string(),
  SizeScaleDirection: z.string(),
  OrdersNumber: z.number(),
  RangeStartBasis: z.string(),
  ConstantRangeStartBasis: z.number(),
  RangeStartBasisMultiplier: z.number(),
  RangeStartOffset: z.string(),
  RangeStartOffsetMultiplier: z.number(),
  RangeEndBasis: z.string(),
  ConstantRangeEndBasis: z.number(),
  RangeEndBasisMultiplier: z.number(),
  RangeEndOffset: z.string(),
  RangeEndOffsetMultiplier: z.number(),
  PriceRandomRate: z.number(),
  CancelDistance: z.string(),
  CancelDistanceMultiplier: z.number(),
  RefreshDistance: z.string(),
  CloseRefreshThresholdMultiplier: z.number(),
  FarRefreshThresholdMultiplier: z.number(),
  MinDistance: z.string(),
  MinDistanceMultiplier: z.number(),
  MaxDistance: z.string(),
  MaxDistanceMultiplier: z.number(),
  MinZScore: z.number(),
  MaxZScore: z.number(),
  WaitTime: z.number(),
  WaitTimeRandomRate: z.number(),
  RefreshTime: z.number(),
  RefreshTimeRandomRate: z.number(),
  RefreshNumber: z.number(),
  RefreshNumberRandomRate: z.number(),
  FreezingTime: z.number(),
  FreezingTimeRandomRate: z.number(),
  PauseTime: z.number(),
  PauseTimeRandomRate: z.number(),
  AutoContinuation: z.boolean(),
});

const GridSchema = z.object({
  AlgoOrderID: z.number(),
  UseAlgoOrder: z.boolean(),
  Hotkey: z.string(),
  Side: z.string(),
  TimeInForce: z.string(),
  BidTotalSize: z.number(),
  BidSizeRandomRate: z.number(),
  BidSizeScaleType: z.string(),
  BidSizeScaleDirection: z.string(),
  AskTotalSize: z.number(),
  AskSizeRandomRate: z.number(),
  AskSizeScaleType: z.string(),
  AskSizeScaleDirection: z.string(),
  OrdersNumber: z.number(),
  LowerRangeBasis: z.string(),
  ConstantLowerRangeBasis: z.number(),
  LowerRangeBasisMultiplier: z.number(),
  LowerRangeOffset: z.string(),
  LowerRangeOffsetMultiplier: z.number(),
  UpperRangeBasis: z.string(),
  ConstantUpperRangeBasis: z.number(),
  UpperRangeBasisMultiplier: z.number(),
  UpperRangeOffset: z.string(),
  UpperRangeOffsetMultiplier: z.number(),
  MinRangeDistance: z.string(),
  MinRangeDistanceMultiplier: z.number(),
  MaxRangeDistance: z.string(),
  MaxRangeDistanceMultiplier: z.number(),
  StartOrderDistance: z.string(),
  StartOrderDistanceMultiplier: z.number(),
  StopOrderDistance: z.string(),
  StopOrderDistanceMultiplier: z.number(),
  CancelDistance: z.string(),
  LowerRangeCancelDistanceMultiplier: z.number(),
  UpperRangeCancelDistanceMultiplier: z.number(),
  MinZScore: z.number(),
  MaxZScore: z.number(),
  WaitTime: z.number(),
  WaitTimeRandomRate: z.number(),
  RefreshTime: z.number(),
  RefreshTimeRandomRate: z.number(),
  RefreshNumber: z.number(),
  RefreshNumberRandomRate: z.number(),
  PauseTime: z.number(),
  PauseTimeRandomRate: z.number(),
  AutoContinuation: z.boolean(),
});

const ImplementationShortfallSchema = z.object({
  AlgoOrderID: z.number(),
  UseAlgoOrder: z.boolean(),
  Hotkey: z.string(),
  MinSize: z.number(),
  MaxSize: z.number(),
  SizeRandomRate: z.number(),
  TargetInventory: z.number(),
  InventoryTolerance: z.number(),
  MinPrice: z.number(),
  MaxPrice: z.number(),
  OrderThrowDepth: z.number(),
  SkewThreshold: z.number(),
  MinZScore: z.number(),
  MaxZScore: z.number(),
  RefreshTime: z.number(),
  RefreshTimeRandomRate: z.number(),
  AutoContinuation: z.boolean(),
});

const AlgoConfigSchema = z.object({
  id: z.string(),
  configName: z.string().min(1, "Name is required"),
  ChaseOrder: ChaseOrderSchema,
  ScaleOrder: ScaleOrderSchema,
  Grid: GridSchema,
  ImplementationShortfall: ImplementationShortfallSchema,
});

import React, { useEffect, useState, useRef } from "react";
import { useConfigsStore } from "./configs.store";
import { useUserStore } from "@/lib/stores/userStore";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function SectionField<T extends object>({
  label,
  value,
  onChange,
  type = "text",
  tooltip,
}: {
  label: string;
  value: any;
  onChange: (v: any) => void;
  type?: string;
  tooltip?: string;
}) {
  if (type === "checkbox") {
    return (
      <div className="flex items-center gap-2 mb-1 w-full">
        <Checkbox
          checked={!!value}
          onCheckedChange={onChange}
          className="h-4 w-4 min-w-4 min-h-4 max-w-4 max-h-4 border border-input rounded-sm"
        />
        <label className="text-xs font-medium select-none cursor-pointer flex items-center gap-1">
          {label}
          {tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help text-muted-foreground">?</span>
              </TooltipTrigger>
              <TooltipContent side="top">{tooltip}</TooltipContent>
            </Tooltip>
          )}
        </label>
      </div>
    );
  }
  return (
    <div className="mb-1 w-full">
      <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
        {label}
        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-muted-foreground">?</span>
            </TooltipTrigger>
            <TooltipContent side="top">{tooltip}</TooltipContent>
          </Tooltip>
        )}
      </label>
      <Input
        type={type}
        value={value}
        onChange={(e) =>
          onChange(type === "number" ? Number(e.target.value) : e.target.value)
        }
        className="h-7 px-2 py-1 text-xs w-full"
      />
    </div>
  );
}

function ChaseOrderForm({
  value,
  onChange,
}: {
  value: ChaseOrderConfig;
  onChange: (v: ChaseOrderConfig) => void;
}) {
  return (
    <div>
      <div className="font-semibold mb-2">Chase Order</div>
      <div className="flex flex-col gap-1 mb-2">
        <SectionField
          label="Enabled"
          type="checkbox"
          value={value.UseAlgoOrder}
          onChange={(v) => onChange({ ...value, UseAlgoOrder: v })}
          tooltip="Включает или выключает использование данного алгоритма."
        />
        <SectionField
          label="Auto Continuation"
          type="checkbox"
          value={value.AutoContinuation}
          onChange={(v) => onChange({ ...value, AutoContinuation: v })}
          tooltip="Автоматически продолжать выполнение после завершения."
        />
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="price">Price</TabsTrigger>
          <TabsTrigger value="distance">Distance</TabsTrigger>
          <TabsTrigger value="zscore">Z-Score</TabsTrigger>
          <TabsTrigger value="time">Time</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                Side
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Сторона сделки: Buy (покупка) или Sell (продажа).
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.Side || ORDER_SIDE_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, Side: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_SIDE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                Type
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Тип заявки: Market, Limit и др.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.Type || ORDER_TYPE_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, Type: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                TimeInForce
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Срок действия заявки: GTC, IOC, FOK и др.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.TimeInForce || TIME_IN_FORCE_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, TimeInForce: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_IN_FORCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="Size"
              type="number"
              value={value.Size}
              onChange={(v) => onChange({ ...value, Size: v })}
              tooltip="Объем заявки в базовой валюте."
            />
            <SectionField
              label="SizeRandomRate"
              type="number"
              value={value.SizeRandomRate}
              onChange={(v) => onChange({ ...value, SizeRandomRate: v })}
              tooltip="Случайное отклонение объема (0-1)."
            />
          </div>
        </TabsContent>
        <TabsContent value="price" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                PriceBasis
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Базис для расчета цены заявки (например, Last, Bid, Ask).
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.PriceBasis || PRICE_BASIS_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, PriceBasis: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_BASIS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="ConstantPriceBasis"
              type="number"
              value={value.ConstantPriceBasis}
              onChange={(v) => onChange({ ...value, ConstantPriceBasis: v })}
              tooltip="Фиксированное смещение к базису цены."
            />
            <SectionField
              label="PriceBasisMultiplier"
              type="number"
              value={value.PriceBasisMultiplier}
              onChange={(v) => onChange({ ...value, PriceBasisMultiplier: v })}
              tooltip="Множитель для базиса цены."
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                PriceOffset
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Тип смещения цены (шаг, процент и др.).
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.PriceOffset || PRICE_OFFSET_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, PriceOffset: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_OFFSET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="PriceOffsetMultiplier"
              type="number"
              value={value.PriceOffsetMultiplier}
              onChange={(v) => onChange({ ...value, PriceOffsetMultiplier: v })}
              tooltip="Величина смещения цены."
            />
            <SectionField
              label="PriceRandomRate"
              type="number"
              value={value.PriceRandomRate}
              onChange={(v) => onChange({ ...value, PriceRandomRate: v })}
              tooltip="Случайное отклонение цены (0-1)."
            />
          </div>
        </TabsContent>
        <TabsContent value="distance" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                CancelDistance
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Дистанция до рынка для отмены заявки.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.CancelDistance || PRICE_OFFSET_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, CancelDistance: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_OFFSET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="CancelDistanceMultiplier"
              type="number"
              value={value.CancelDistanceMultiplier}
              onChange={(v) =>
                onChange({ ...value, CancelDistanceMultiplier: v })
              }
              tooltip="Множитель дистанции отмены."
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                RefreshDistance
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Дистанция для обновления заявки.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={
                  value.RefreshDistance || REFRESH_DISTANCE_OPTIONS[0].value
                }
                onValueChange={(v) =>
                  onChange({ ...value, RefreshDistance: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MIN_DISTANCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="MinDistanceMultiplier"
              type="number"
              value={value.MinDistanceMultiplier}
              onChange={(v) => onChange({ ...value, MinDistanceMultiplier: v })}
              tooltip="Множитель минимальной дистанции."
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                MaxDistance
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Максимальная дистанция между заявками.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.MaxDistance || MAX_DISTANCE_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, MaxDistance: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MAX_DISTANCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="MaxDistanceMultiplier"
              type="number"
              value={value.MaxDistanceMultiplier}
              onChange={(v) => onChange({ ...value, MaxDistanceMultiplier: v })}
              tooltip="Множитель максимальной дистанции."
            />
          </div>
        </TabsContent>
        <TabsContent value="zscore" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="MinZScore"
              type="number"
              value={value.MinZScore}
              onChange={(v) => onChange({ ...value, MinZScore: v })}
              tooltip="Минимальное значение Z-оценки для фильтрации."
            />
            <SectionField
              label="MaxZScore"
              type="number"
              value={value.MaxZScore}
              onChange={(v) => onChange({ ...value, MaxZScore: v })}
              tooltip="Максимальное значение Z-оценки для фильтрации."
            />
          </div>
        </TabsContent>
        <TabsContent value="time" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="WaitTime"
              type="number"
              value={value.WaitTime}
              onChange={(v) => onChange({ ...value, WaitTime: v })}
              tooltip="Время ожидания перед выставлением."
            />
            <SectionField
              label="WaitTimeRandomRate"
              type="number"
              value={value.WaitTimeRandomRate}
              onChange={(v) => onChange({ ...value, WaitTimeRandomRate: v })}
              tooltip="Случайное отклонение времени ожидания."
            />
            <SectionField
              label="RefreshTime"
              type="number"
              value={value.RefreshTime}
              onChange={(v) => onChange({ ...value, RefreshTime: v })}
              tooltip="Время между обновлениями заявки."
            />
            <SectionField
              label="RefreshTimeRandomRate"
              type="number"
              value={value.RefreshTimeRandomRate}
              onChange={(v) => onChange({ ...value, RefreshTimeRandomRate: v })}
              tooltip="Случайное отклонение времени обновления."
            />
            <SectionField
              label="RefreshNumber"
              type="number"
              value={value.RefreshNumber}
              onChange={(v) => onChange({ ...value, RefreshNumber: v })}
              tooltip="Количество обновлений заявки."
            />
            <SectionField
              label="RefreshNumberRandomRate"
              type="number"
              value={value.RefreshNumberRandomRate}
              onChange={(v) =>
                onChange({ ...value, RefreshNumberRandomRate: v })
              }
              tooltip="Случайное отклонение количества обновлений."
            />
            <SectionField
              label="FreezingTime"
              type="number"
              value={value.FreezingTime}
              onChange={(v) => onChange({ ...value, FreezingTime: v })}
              tooltip="Время заморозки после действия."
            />
            <SectionField
              label="FreezingTimeRandomRate"
              type="number"
              value={value.FreezingTimeRandomRate}
              onChange={(v) =>
                onChange({ ...value, FreezingTimeRandomRate: v })
              }
              tooltip="Случайное отклонение времени заморозки."
            />
            <SectionField
              label="PauseTime"
              type="number"
              value={value.PauseTime}
              onChange={(v) => onChange({ ...value, PauseTime: v })}
              tooltip="Пауза между действиями."
            />
            <SectionField
              label="PauseTimeRandomRate"
              type="number"
              value={value.PauseTimeRandomRate}
              onChange={(v) => onChange({ ...value, PauseTimeRandomRate: v })}
              tooltip="Случайное отклонение паузы."
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ScaleOrderForm({
  value,
  onChange,
}: {
  value: ScaleOrderConfig;
  onChange: (v: ScaleOrderConfig) => void;
}) {
  return (
    <div>
      <div className="font-semibold mb-2">Scale Order</div>
      <div className="flex flex-col gap-1 mb-2">
        <SectionField
          label="Enabled"
          type="checkbox"
          value={value.UseAlgoOrder}
          onChange={(v) => onChange({ ...value, UseAlgoOrder: v })}
        />
        <SectionField
          label="Auto Continuation"
          type="checkbox"
          value={value.AutoContinuation}
          onChange={(v) => onChange({ ...value, AutoContinuation: v })}
        />
      </div>
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="size">Size</TabsTrigger>
          <TabsTrigger value="range_start">Range Start</TabsTrigger>
          <TabsTrigger value="range_end">Range End</TabsTrigger>
          <TabsTrigger value="price_distance">Price & Distance</TabsTrigger>
          <TabsTrigger value="zscore">Z-Score</TabsTrigger>
          <TabsTrigger value="time">Time</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">Side</label>
              <Select
                value={value.Side || ORDER_SIDE_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, Side: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_SIDE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">TimeInForce</label>
              <Select
                value={value.TimeInForce || TIME_IN_FORCE_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, TimeInForce: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_IN_FORCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="OrdersNumber"
              type="number"
              value={value.OrdersNumber}
              onChange={(v) => onChange({ ...value, OrdersNumber: v })}
            />
          </div>
        </TabsContent>
        <TabsContent value="size" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="TotalSize"
              type="number"
              value={value.TotalSize}
              onChange={(v) => onChange({ ...value, TotalSize: v })}
            />
            <SectionField
              label="SizeRandomRate"
              type="number"
              value={value.SizeRandomRate}
              onChange={(v) => onChange({ ...value, SizeRandomRate: v })}
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">
                SizeScaleType
              </label>
              <Select
                value={value.SizeScaleType || SCALE_TYPE_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, SizeScaleType: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCALE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">
                SizeScaleDirection
              </label>
              <Select
                value={
                  value.SizeScaleDirection || SCALE_DIRECTION_OPTIONS[0].value
                }
                onValueChange={(v) =>
                  onChange({ ...value, SizeScaleDirection: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCALE_DIRECTION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="range_start" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">
                RangeStartBasis
              </label>
              <Select
                value={value.RangeStartBasis || PRICE_BASIS_OPTIONS[0].value}
                onValueChange={(v) =>
                  onChange({ ...value, RangeStartBasis: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_BASIS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="ConstantRangeStartBasis"
              type="number"
              value={value.ConstantRangeStartBasis}
              onChange={(v) =>
                onChange({ ...value, ConstantRangeStartBasis: v })
              }
            />
            <SectionField
              label="RangeStartBasisMultiplier"
              type="number"
              value={value.RangeStartBasisMultiplier}
              onChange={(v) =>
                onChange({ ...value, RangeStartBasisMultiplier: v })
              }
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">
                RangeStartOffset
              </label>
              <Select
                value={value.RangeStartOffset || PRICE_OFFSET_OPTIONS[0].value}
                onValueChange={(v) =>
                  onChange({ ...value, RangeStartOffset: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_OFFSET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="RangeStartOffsetMultiplier"
              type="number"
              value={value.RangeStartOffsetMultiplier}
              onChange={(v) =>
                onChange({ ...value, RangeStartOffsetMultiplier: v })
              }
            />
          </div>
        </TabsContent>
        <TabsContent value="range_end" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">
                RangeEndBasis
              </label>
              <Select
                value={value.RangeEndBasis || PRICE_BASIS_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, RangeEndBasis: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_BASIS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="ConstantRangeEndBasis"
              type="number"
              value={value.ConstantRangeEndBasis}
              onChange={(v) => onChange({ ...value, ConstantRangeEndBasis: v })}
            />
            <SectionField
              label="RangeEndBasisMultiplier"
              type="number"
              value={value.RangeEndBasisMultiplier}
              onChange={(v) =>
                onChange({ ...value, RangeEndBasisMultiplier: v })
              }
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">
                RangeEndOffset
              </label>
              <Select
                value={value.RangeEndOffset || PRICE_OFFSET_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, RangeEndOffset: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_OFFSET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="RangeEndOffsetMultiplier"
              type="number"
              value={value.RangeEndOffsetMultiplier}
              onChange={(v) =>
                onChange({ ...value, RangeEndOffsetMultiplier: v })
              }
            />
          </div>
        </TabsContent>
        <TabsContent value="price_distance" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="PriceRandomRate"
              type="number"
              value={value.PriceRandomRate}
              onChange={(v) => onChange({ ...value, PriceRandomRate: v })}
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">
                CancelDistance
              </label>
              <Select
                value={value.CancelDistance || PRICE_OFFSET_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, CancelDistance: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_OFFSET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="CancelDistanceMultiplier"
              type="number"
              value={value.CancelDistanceMultiplier}
              onChange={(v) =>
                onChange({ ...value, CancelDistanceMultiplier: v })
              }
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">
                RefreshDistance
              </label>
              <Select
                value={
                  value.RefreshDistance || REFRESH_DISTANCE_OPTIONS[0].value
                }
                onValueChange={(v) =>
                  onChange({ ...value, RefreshDistance: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REFRESH_DISTANCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="CloseRefreshThresholdMultiplier"
              type="number"
              value={value.CloseRefreshThresholdMultiplier}
              onChange={(v) =>
                onChange({ ...value, CloseRefreshThresholdMultiplier: v })
              }
            />
            <SectionField
              label="FarRefreshThresholdMultiplier"
              type="number"
              value={value.FarRefreshThresholdMultiplier}
              onChange={(v) =>
                onChange({ ...value, FarRefreshThresholdMultiplier: v })
              }
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">MinDistance</label>
              <Select
                value={value.MinDistance || MIN_DISTANCE_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, MinDistance: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MIN_DISTANCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="MinDistanceMultiplier"
              type="number"
              value={value.MinDistanceMultiplier}
              onChange={(v) => onChange({ ...value, MinDistanceMultiplier: v })}
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">MaxDistance</label>
              <Select
                value={value.MaxDistance || MAX_DISTANCE_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, MaxDistance: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MAX_DISTANCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="MaxDistanceMultiplier"
              type="number"
              value={value.MaxDistanceMultiplier}
              onChange={(v) => onChange({ ...value, MaxDistanceMultiplier: v })}
            />
          </div>
        </TabsContent>
        <TabsContent value="zscore" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="MinZScore"
              type="number"
              value={value.MinZScore}
              onChange={(v) => onChange({ ...value, MinZScore: v })}
              tooltip="Минимальное значение Z-оценки для фильтрации заявок."
            />
            <SectionField
              label="MaxZScore"
              type="number"
              value={value.MaxZScore}
              onChange={(v) => onChange({ ...value, MaxZScore: v })}
              tooltip="Максимальное значение Z-оценки для фильтрации заявок."
            />
          </div>
        </TabsContent>
        <TabsContent value="time" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="WaitTime"
              type="number"
              value={value.WaitTime}
              onChange={(v) => onChange({ ...value, WaitTime: v })}
              tooltip="Время ожидания перед выставлением заявок (мс)."
            />
            <SectionField
              label="WaitTimeRandomRate"
              type="number"
              value={value.WaitTimeRandomRate}
              onChange={(v) => onChange({ ...value, WaitTimeRandomRate: v })}
              tooltip="Случайное отклонение времени ожидания."
            />
            <SectionField
              label="RefreshTime"
              type="number"
              value={value.RefreshTime}
              onChange={(v) => onChange({ ...value, RefreshTime: v })}
              tooltip="Время между обновлениями заявок (мс)."
            />
            <SectionField
              label="RefreshTimeRandomRate"
              type="number"
              value={value.RefreshTimeRandomRate}
              onChange={(v) => onChange({ ...value, RefreshTimeRandomRate: v })}
              tooltip="Случайное отклонение времени обновления."
            />
            <SectionField
              label="RefreshNumber"
              type="number"
              value={value.RefreshNumber}
              onChange={(v) => onChange({ ...value, RefreshNumber: v })}
              tooltip="Количество обновлений заявки."
            />
            <SectionField
              label="RefreshNumberRandomRate"
              type="number"
              value={value.RefreshNumberRandomRate}
              onChange={(v) =>
                onChange({ ...value, RefreshNumberRandomRate: v })
              }
              tooltip="Случайное отклонение количества обновлений."
            />
            <SectionField
              label="PauseTime"
              type="number"
              value={value.PauseTime}
              onChange={(v) => onChange({ ...value, PauseTime: v })}
              tooltip="Пауза между действиями (мс)."
            />
            <SectionField
              label="PauseTimeRandomRate"
              type="number"
              value={value.PauseTimeRandomRate}
              onChange={(v) => onChange({ ...value, PauseTimeRandomRate: v })}
              tooltip="Случайное отклонение паузы."
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GridForm({
  value,
  onChange,
}: {
  value: GridConfig;
  onChange: (v: GridConfig) => void;
}) {
  return (
    <div>
      <div className="font-semibold mb-2">Grid</div>
      <div className="flex flex-col gap-1 mb-2">
        <SectionField
          label="Enabled"
          type="checkbox"
          value={value.UseAlgoOrder}
          onChange={(v) => onChange({ ...value, UseAlgoOrder: v })}
          tooltip="Включает или выключает использование данного алгоритма."
        />
        <SectionField
          label="Auto Continuation"
          type="checkbox"
          value={value.AutoContinuation}
          onChange={(v) => onChange({ ...value, AutoContinuation: v })}
          tooltip="Автоматически продолжать выполнение после завершения."
        />
      </div>
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="bid_size">Bid Size</TabsTrigger>
          <TabsTrigger value="ask_size">Ask Size</TabsTrigger>
          <TabsTrigger value="range">Range</TabsTrigger>
          <TabsTrigger value="order_distance">Order Distance</TabsTrigger>
          <TabsTrigger value="cancel_distance">Cancel Distance</TabsTrigger>
          <TabsTrigger value="zscore">Z-Score</TabsTrigger>
          <TabsTrigger value="time">Time</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                Side
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Сторона сетки: Bid, Ask или Neutral.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.Side || GRID_SIDE_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, Side: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRID_SIDE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                TimeInForce
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Срок действия заявок: GTC, IOC, FOK и др.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.TimeInForce || TIME_IN_FORCE_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, TimeInForce: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_IN_FORCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="OrdersNumber"
              type="number"
              value={value.OrdersNumber}
              onChange={(v) => onChange({ ...value, OrdersNumber: v })}
              tooltip="Общее количество заявок в гриде."
            />
          </div>
        </TabsContent>
        <TabsContent value="bid_size" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="BidTotalSize"
              type="number"
              value={value.BidTotalSize}
              onChange={(v) => onChange({ ...value, BidTotalSize: v })}
              tooltip="Общий объём заявок на покупку (Bid)."
            />
            <SectionField
              label="BidSizeRandomRate"
              type="number"
              value={value.BidSizeRandomRate}
              onChange={(v) => onChange({ ...value, BidSizeRandomRate: v })}
              tooltip="Случайное отклонение объёма Bid (0-1)."
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                BidSizeScaleType
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Тип масштабирования объёма Bid: linear, exponential и др.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.BidSizeScaleType || SCALE_TYPE_OPTIONS[0].value}
                onValueChange={(v) =>
                  onChange({ ...value, BidSizeScaleType: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCALE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                BidSizeScaleDirection
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Направление масштабирования объёма Bid: увеличивать к краю
                    или к центру.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={
                  value.BidSizeScaleDirection ||
                  SCALE_DIRECTION_OPTIONS[0].value
                }
                onValueChange={(v) =>
                  onChange({ ...value, BidSizeScaleDirection: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCALE_DIRECTION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="ask_size" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="AskTotalSize"
              type="number"
              value={value.AskTotalSize}
              onChange={(v) => onChange({ ...value, AskTotalSize: v })}
              tooltip="Общий объём заявок на продажу (Ask)."
            />
            <SectionField
              label="AskSizeRandomRate"
              type="number"
              value={value.AskSizeRandomRate}
              onChange={(v) => onChange({ ...value, AskSizeRandomRate: v })}
              tooltip="Случайное отклонение объёма Ask (0-1)."
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                AskSizeScaleType
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Тип масштабирования объёма Ask: linear, exponential и др.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.AskSizeScaleType || SCALE_TYPE_OPTIONS[0].value}
                onValueChange={(v) =>
                  onChange({ ...value, AskSizeScaleType: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCALE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                AskSizeScaleDirection
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Направление масштабирования объёма Ask: увеличивать к краю
                    или к центру.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={
                  value.AskSizeScaleDirection ||
                  SCALE_DIRECTION_OPTIONS[0].value
                }
                onValueChange={(v) =>
                  onChange({ ...value, AskSizeScaleDirection: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCALE_DIRECTION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="range" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                LowerRangeBasis
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Базис для нижней границы диапазона (например, Last, Bid,
                    Ask).
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.LowerRangeBasis || PRICE_BASIS_OPTIONS[0].value}
                onValueChange={(v) =>
                  onChange({ ...value, LowerRangeBasis: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_BASIS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="ConstantLowerRangeBasis"
              type="number"
              value={value.ConstantLowerRangeBasis}
              onChange={(v) =>
                onChange({ ...value, ConstantLowerRangeBasis: v })
              }
              tooltip="Фиксированное смещение к нижнему базису диапазона."
            />
            <SectionField
              label="LowerRangeBasisMultiplier"
              type="number"
              value={value.LowerRangeBasisMultiplier}
              onChange={(v) =>
                onChange({ ...value, LowerRangeBasisMultiplier: v })
              }
              tooltip="Множитель для нижнего базиса диапазона."
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                LowerRangeOffset
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Тип смещения нижней границы диапазона (шаг, процент и др.).
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.LowerRangeOffset || PRICE_OFFSET_OPTIONS[0].value}
                onValueChange={(v) =>
                  onChange({ ...value, LowerRangeOffset: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_OFFSET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="LowerRangeOffsetMultiplier"
              type="number"
              value={value.LowerRangeOffsetMultiplier}
              onChange={(v) =>
                onChange({ ...value, LowerRangeOffsetMultiplier: v })
              }
              tooltip="Величина смещения нижней границы диапазона."
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                UpperRangeBasis
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Базис для верхней границы диапазона (например, Last, Bid,
                    Ask).
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.UpperRangeBasis || PRICE_BASIS_OPTIONS[0].value}
                onValueChange={(v) =>
                  onChange({ ...value, UpperRangeBasis: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_BASIS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="ConstantUpperRangeBasis"
              type="number"
              value={value.ConstantUpperRangeBasis}
              onChange={(v) =>
                onChange({ ...value, ConstantUpperRangeBasis: v })
              }
              tooltip="Фиксированное смещение к верхнему базису диапазона."
            />
            <SectionField
              label="UpperRangeBasisMultiplier"
              type="number"
              value={value.UpperRangeBasisMultiplier}
              onChange={(v) =>
                onChange({ ...value, UpperRangeBasisMultiplier: v })
              }
              tooltip="Множитель для верхнего базиса диапазона."
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                UpperRangeOffset
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Тип смещения верхней границы диапазона (шаг, процент и др.).
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.UpperRangeOffset || PRICE_OFFSET_OPTIONS[0].value}
                onValueChange={(v) =>
                  onChange({ ...value, UpperRangeOffset: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_OFFSET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="UpperRangeOffsetMultiplier"
              type="number"
              value={value.UpperRangeOffsetMultiplier}
              onChange={(v) =>
                onChange({ ...value, UpperRangeOffsetMultiplier: v })
              }
              tooltip="Величина смещения верхней границы диапазона."
            />
          </div>
        </TabsContent>
        <TabsContent value="order_distance" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                StartOrderDistance
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Дистанция от границы диапазона до первой заявки.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={
                  value.StartOrderDistance || PRICE_OFFSET_OPTIONS[0].value
                }
                onValueChange={(v) =>
                  onChange({ ...value, StartOrderDistance: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_OFFSET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="StartOrderDistanceMultiplier"
              type="number"
              value={value.StartOrderDistanceMultiplier}
              onChange={(v) =>
                onChange({ ...value, StartOrderDistanceMultiplier: v })
              }
              tooltip="Множитель дистанции до первой заявки."
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                StopOrderDistance
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Дистанция от границы диапазона до последней заявки.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.StopOrderDistance || PRICE_OFFSET_OPTIONS[0].value}
                onValueChange={(v) =>
                  onChange({ ...value, StopOrderDistance: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_OFFSET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="StopOrderDistanceMultiplier"
              type="number"
              value={value.StopOrderDistanceMultiplier}
              onChange={(v) =>
                onChange({ ...value, StopOrderDistanceMultiplier: v })
              }
              tooltip="Множитель дистанции до последней заявки."
            />
          </div>
        </TabsContent>
        <TabsContent value="cancel_distance" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                CancelDistance
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground">?</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Тип дистанции до рынка, при которой отменяется заявка.
                  </TooltipContent>
                </Tooltip>
              </label>
              <Select
                value={value.CancelDistance || PRICE_OFFSET_OPTIONS[0].value}
                onValueChange={(v) => onChange({ ...value, CancelDistance: v })}
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_OFFSET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="LowerRangeCancelDistanceMultiplier"
              type="number"
              value={value.LowerRangeCancelDistanceMultiplier}
              onChange={(v) =>
                onChange({ ...value, LowerRangeCancelDistanceMultiplier: v })
              }
              tooltip="Множитель дистанции отмены для нижней границы диапазона."
            />
            <SectionField
              label="UpperRangeCancelDistanceMultiplier"
              type="number"
              value={value.UpperRangeCancelDistanceMultiplier}
              onChange={(v) =>
                onChange({ ...value, UpperRangeCancelDistanceMultiplier: v })
              }
              tooltip="Множитель дистанции отмены для верхней границы диапазона."
            />
          </div>
        </TabsContent>
        <TabsContent value="zscore" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="MinZScore"
              type="number"
              value={value.MinZScore}
              onChange={(v) => onChange({ ...value, MinZScore: v })}
              tooltip="Минимальное значение Z-оценки для фильтрации заявок."
            />
            <SectionField
              label="MaxZScore"
              type="number"
              value={value.MaxZScore}
              onChange={(v) => onChange({ ...value, MaxZScore: v })}
              tooltip="Максимальное значение Z-оценки для фильтрации заявок."
            />
          </div>
        </TabsContent>
        <TabsContent value="time" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="WaitTime"
              type="number"
              value={value.WaitTime}
              onChange={(v) => onChange({ ...value, WaitTime: v })}
              tooltip="Время ожидания перед выставлением заявок (мс)."
            />
            <SectionField
              label="WaitTimeRandomRate"
              type="number"
              value={value.WaitTimeRandomRate}
              onChange={(v) => onChange({ ...value, WaitTimeRandomRate: v })}
              tooltip="Случайное отклонение времени ожидания."
            />
            <SectionField
              label="RefreshTime"
              type="number"
              value={value.RefreshTime}
              onChange={(v) => onChange({ ...value, RefreshTime: v })}
              tooltip="Время между обновлениями заявок (мс)."
            />
            <SectionField
              label="RefreshTimeRandomRate"
              type="number"
              value={value.RefreshTimeRandomRate}
              onChange={(v) => onChange({ ...value, RefreshTimeRandomRate: v })}
              tooltip="Случайное отклонение времени обновления."
            />
            <SectionField
              label="RefreshNumber"
              type="number"
              value={value.RefreshNumber}
              onChange={(v) => onChange({ ...value, RefreshNumber: v })}
              tooltip="Количество обновлений заявки."
            />
            <SectionField
              label="RefreshNumberRandomRate"
              type="number"
              value={value.RefreshNumberRandomRate}
              onChange={(v) =>
                onChange({ ...value, RefreshNumberRandomRate: v })
              }
              tooltip="Случайное отклонение количества обновлений."
            />
            <SectionField
              label="PauseTime"
              type="number"
              value={value.PauseTime}
              onChange={(v) => onChange({ ...value, PauseTime: v })}
              tooltip="Пауза между действиями (мс)."
            />
            <SectionField
              label="PauseTimeRandomRate"
              type="number"
              value={value.PauseTimeRandomRate}
              onChange={(v) => onChange({ ...value, PauseTimeRandomRate: v })}
              tooltip="Случайное отклонение паузы."
            />
          </div>
        </TabsContent>
        <TabsContent value="range" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5 flex items-center gap-1">
                MinRangeDistance
              </label>
              <Select
                value={value.MinRangeDistance || PRICE_OFFSET_OPTIONS[0].value}
                onValueChange={(v) =>
                  onChange({ ...value, MinRangeDistance: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_OFFSET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="MinRangeDistanceMultiplier"
              type="number"
              value={value.MinRangeDistanceMultiplier}
              onChange={(v) =>
                onChange({ ...value, MinRangeDistanceMultiplier: v })
              }
              tooltip="Множитель минимальной дистанции между заявками."
            />
            <div className="mb-1 w-full">
              <label className="text-xs font-medium mb-0.5">
                MaxRangeDistance
              </label>
              <Select
                value={value.MaxRangeDistance || PRICE_OFFSET_OPTIONS[0].value}
                onValueChange={(v) =>
                  onChange({ ...value, MaxRangeDistance: v })
                }
              >
                <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_OFFSET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <SectionField
              label="MaxRangeDistanceMultiplier"
              type="number"
              value={value.MaxRangeDistanceMultiplier}
              onChange={(v) =>
                onChange({ ...value, MaxRangeDistanceMultiplier: v })
              }
              tooltip="Множитель максимальной дистанции между заявками."
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ImplementationShortfallForm({
  value,
  onChange,
}: {
  value: ImplementationShortfallConfig;
  onChange: (v: ImplementationShortfallConfig) => void;
}) {
  return (
    <div>
      <div className="font-semibold mb-2">ImplementationShortfall</div>
      <div className="flex flex-col gap-1 mb-2">
        <SectionField
          label="Enabled"
          type="checkbox"
          value={value.UseAlgoOrder}
          onChange={(v) => onChange({ ...value, UseAlgoOrder: v })}
        />
        <SectionField
          label="Auto Continuation"
          type="checkbox"
          value={value.AutoContinuation}
          onChange={(v) => onChange({ ...value, AutoContinuation: v })}
        />
      </div>
      <Tabs defaultValue="size" className="w-full">
        <TabsList>
          <TabsTrigger value="size">Size</TabsTrigger>
          <TabsTrigger value="inventory_price">Inventory & Price</TabsTrigger>
          <TabsTrigger value="zscore">Z-Score</TabsTrigger>
          <TabsTrigger value="time">Time</TabsTrigger>
        </TabsList>

        <TabsContent value="size" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="MinSize"
              type="number"
              value={value.MinSize}
              onChange={(v) => onChange({ ...value, MinSize: v })}
            />
            <SectionField
              label="MaxSize"
              type="number"
              value={value.MaxSize}
              onChange={(v) => onChange({ ...value, MaxSize: v })}
            />
            <SectionField
              label="SizeRandomRate"
              type="number"
              value={value.SizeRandomRate}
              onChange={(v) => onChange({ ...value, SizeRandomRate: v })}
            />
          </div>
        </TabsContent>
        <TabsContent value="inventory_price" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="TargetInventory"
              type="number"
              value={value.TargetInventory}
              onChange={(v) => onChange({ ...value, TargetInventory: v })}
            />
            <SectionField
              label="InventoryTolerance"
              type="number"
              value={value.InventoryTolerance}
              onChange={(v) => onChange({ ...value, InventoryTolerance: v })}
            />
            <SectionField
              label="MinPrice"
              type="number"
              value={value.MinPrice}
              onChange={(v) => onChange({ ...value, MinPrice: v })}
            />
            <SectionField
              label="MaxPrice"
              type="number"
              value={value.MaxPrice}
              onChange={(v) => onChange({ ...value, MaxPrice: v })}
            />
            <SectionField
              label="OrderThrowDepth"
              type="number"
              value={value.OrderThrowDepth}
              onChange={(v) => onChange({ ...value, OrderThrowDepth: v })}
            />
            <SectionField
              label="SkewThreshold"
              type="number"
              value={value.SkewThreshold}
              onChange={(v) => onChange({ ...value, SkewThreshold: v })}
            />
          </div>
        </TabsContent>
        <TabsContent value="zscore" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="MinZScore"
              type="number"
              value={value.MinZScore}
              onChange={(v) => onChange({ ...value, MinZScore: v })}
              tooltip="Минимальное значение Z-оценки для фильтрации заявок."
            />
            <SectionField
              label="MaxZScore"
              type="number"
              value={value.MaxZScore}
              onChange={(v) => onChange({ ...value, MaxZScore: v })}
            />
          </div>
        </TabsContent>
        <TabsContent value="time" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
            <SectionField
              label="RefreshTime"
              type="number"
              value={value.RefreshTime}
              onChange={(v) => onChange({ ...value, RefreshTime: v })}
            />
            <SectionField
              label="RefreshTimeRandomRate"
              type="number"
              value={value.RefreshTimeRandomRate}
              onChange={(v) => onChange({ ...value, RefreshTimeRandomRate: v })}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ConfigForm({
  initialConfig,
  onSave,
  onCancel,
}: {
  initialConfig?: AlgoConfig;
  onSave: (config: AlgoConfig) => void;
  onCancel: () => void;
}) {
  const [config, setConfig] = useState<AlgoConfig>(
    initialConfig || DEFAULT_ALGO_CONFIG,
  );
  const [selectedAlgoType, setSelectedAlgoType] =
    useState<string>("ChaseOrder");
  const [selectedPreset, setSelectedPreset] = useState<string>("custom");

  // Presets array
  const presetsOptions = [
    { label: "None (Custom)", value: "custom" },
    {
      label: "SF Default Grid",
      value: "sfDefaultGrid",
      config: { Grid: sfDefaultGrid },
    },
    // Add more presets here
  ];

  const handlePresetChange = (value: string) => {
    setSelectedPreset(value);
    if (value === "custom") {
      setConfig((prev) => ({
        ...DEFAULT_ALGO_CONFIG,
        id: prev.id,
        configName: prev.configName,
      }));
      return;
    }
    const preset = presetsOptions.find((p) => p.value === value);
    if (preset && preset.config) {
      const merged = merge({}, DEFAULT_ALGO_CONFIG, preset.config);
      setConfig((prev) => ({
        ...merged,
        id: prev.id,
        configName: prev.configName,
      }));
    }
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(config);
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <form onSubmit={handleSave} className="space-y-4 w-full">
      {/* Presets Dropdown */}
      <div>
        <label className="mb-0.5 font-medium text-xs">Preset</label>
        <Select value={selectedPreset} onValueChange={handlePresetChange}>
          <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
            <SelectValue placeholder="Select preset" />
          </SelectTrigger>
          <SelectContent>
            {presetsOptions.map((preset) => (
              <SelectItem key={preset.value} value={preset.value}>
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* Name Field */}
      <div>
        <label className="mb-0.5 font-medium text-xs">Name</label>
        <Input
          value={config.configName}
          onChange={(e) => setConfig({ ...config, configName: e.target.value })}
          required
          className="h-7 px-2 py-1 text-xs w-full"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium mb-0.5">Algo Type</label>
        <Select value={selectedAlgoType} onValueChange={setSelectedAlgoType}>
          <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
            <SelectValue placeholder="Select Algo Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ChaseOrder">Chase Order</SelectItem>
            <SelectItem value="ScaleOrder">Scale Order</SelectItem>
            <SelectItem value="Grid">Grid</SelectItem>
            <SelectItem value="ImplementationShortfall">
              Implementation Shortfall
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      {/* Conditionally rendered Algo Forms */}
      {selectedAlgoType === "ChaseOrder" && (
        <ChaseOrderForm
          key="ChaseOrderForm"
          value={config.ChaseOrder}
          onChange={(v) => setConfig((c) => ({ ...c, ChaseOrder: v }))}
        />
      )}
      {selectedAlgoType === "ScaleOrder" && (
        <ScaleOrderForm
          key="ScaleOrderForm"
          value={config.ScaleOrder}
          onChange={(v) => setConfig((c) => ({ ...c, ScaleOrder: v }))}
        />
      )}
      {selectedAlgoType === "Grid" && (
        <GridForm
          key="GridForm"
          value={config.Grid}
          onChange={(v) => setConfig((c) => ({ ...c, Grid: v }))}
        />
      )}
      {selectedAlgoType === "ImplementationShortfall" && (
        <ImplementationShortfallForm
          key="ImplementationShortfallForm"
          value={config.ImplementationShortfall}
          onChange={(v) =>
            setConfig((c) => ({ ...c, ImplementationShortfall: v }))
          }
        />
      )}
      <DialogFooter>
        <Button type="submit" size="xs">
          Save
        </Button>
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </DialogFooter>
    </form>
  );
}

export function Configs() {
  const { user } = useUserStore();
  const userId = user?.uid;
  const {
    configs,
    isLoading,
    hasError,
    loadConfigs,
    addConfig,
    updateConfig,
    deleteConfig,
  } = useConfigsStore();
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<AlgoConfig | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importedConfig, setImportedConfig] = useState<AlgoConfig | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (userId) {
      loadConfigs(userId);
    }
  }, [userId, loadConfigs]);

  const handleSave = async (config: AlgoConfig) => {
    if (!userId) {
      return;
    }
    if (editingConfig) {
      await updateConfig(userId, config);
    } else {
      await addConfig(userId, config);
    }
    setShowForm(false);
    setEditingConfig(null);
  };

  const handleDelete = async (id: string) => {
    if (!userId) {
      return;
    }
    await deleteConfig(userId, id);
    setDeletingId(null);
  };

  // Export a single config as JSON
  const handleExport = (cfg: AlgoConfig) => {
    const dataStr = JSON.stringify(cfg, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `algo-config-${cfg.configName || cfg.id}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import a new config (add)
  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        setImportedConfig(json);
        setShowImportConfirm(true);
      } catch {
        alert("Invalid JSON file.");
      }
    };
    reader.readAsText(file);
  };

  const handleImportConfirm = async () => {
    if (!userId || !importedConfig) {
      return;
    }
    // Add as new config with a new id
    const newConfig = { ...importedConfig, id: uuidv4() };
    await addConfig(userId, newConfig);
    setShowImportConfirm(false);
    setImportedConfig(null);
    loadConfigs(userId);
  };

  return (
    <TooltipProvider>
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2">
            <Button
              size="xxs"
              variant="ghost"
              aria-label="Import"
              className="h-5 w-5 p-0.5"
              onClick={handleImportClick}
            >
              <Download className="w-3.5 h-3.5" />
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              size="xxs"
              variant="ghost"
              aria-label="New Config"
              className="h-5 w-5 p-0.5"
              onClick={() => {
                setShowForm(true);
                setEditingConfig(null);
              }}
            >
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        {isLoading && <div>Loading...</div>}
        {hasError && <div className="text-red-600">Error loading configs.</div>}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {configs.map((cfg) => (
              <TableRow
                key={cfg.id}
                onClick={() => {
                  setEditingConfig(cfg);
                  setShowForm(true);
                }}
                className="cursor-pointer hover:bg-muted"
              >
                <TableCell>{cfg.configName}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button
                      size="xxs"
                      variant="ghost"
                      aria-label="Export"
                      className="h-5 w-5 p-0.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExport(cfg);
                      }}
                    >
                      <Upload className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="xxs"
                      variant="ghost"
                      aria-label="Edit"
                      className="h-5 w-5 p-0.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingConfig(cfg);
                        setShowForm(true);
                      }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="xxs"
                          variant="ghost"
                          aria-label="Delete"
                          className="h-5 w-5 p-0.5 text-destructive hover:bg-destructive/10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Trash className="w-3.5 h-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Config</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this config?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(cfg.id)}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Dialog
          open={showForm}
          onOpenChange={(open) => {
            setShowForm(open);
            if (!open) {
              setEditingConfig(null);
            }
          }}
        >
          <DialogContent className="max-w-4xl w-full">
            <DialogHeader>
              <DialogTitle>{editingConfig ? "Edit" : "New"} Config</DialogTitle>
              <DialogDescription>
                Configure your trading algorithm settings. Fill out the form and
                save to apply changes.
              </DialogDescription>
            </DialogHeader>
            <ConfigForm
              initialConfig={editingConfig || undefined}
              onSave={handleSave}
              onCancel={() => {
                setShowForm(false);
                setEditingConfig(null);
              }}
            />
          </DialogContent>
        </Dialog>
        <AlertDialog
          open={showImportConfirm}
          onOpenChange={setShowImportConfirm}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Import Config</AlertDialogTitle>
              <AlertDialogDescription>
                This will add a new config. Continue?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setShowImportConfirm(false)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleImportConfirm}>
                Import
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}
